import { CODE_PAGES, encodeText } from './codepages.js';

const ESC = 0x1b;
const GS = 0x1d;
const DLE = 0x10;
const LF = 0x0a;

/**
 * Low-level ESC/POS command builder (Epson-compatible command set, which nearly every thermal
 * printer — TVS, Posiflex, Rugtek, Xprinter, Everycom… — accepts).
 * Every method returns `this`; `bytes()` returns a Uint8Array.
 */
export class EscPosEncoder {
  constructor({ codePage = 'cp437' } = {}) {
    this.codePage = CODE_PAGES[codePage] ? codePage : 'cp437';
    this.parts = [];
  }

  raw(bytes) {
    // A loop, not push(...bytes): raster images can be tens of KB (spread hits arg limits).
    for (let i = 0; i < bytes.length; i++) this.parts.push(bytes[i]);
    return this;
  }

  /** ESC @ — reset formatting, then select the code page (ESC t n). */
  initialize() {
    return this.raw([ESC, 0x40, ESC, 0x74, CODE_PAGES[this.codePage].select]);
  }

  text(str) {
    return this.raw(encodeText(str, this.codePage));
  }

  newline(n = 1) {
    for (let i = 0; i < n; i++) this.parts.push(LF);
    return this;
  }

  /** ESC a n — 0 left, 1 center, 2 right. Applies to the start of the next line. */
  align(a = 'left') {
    return this.raw([ESC, 0x61, a === 'center' ? 1 : a === 'right' ? 2 : 0]);
  }

  /** ESC E n */
  bold(on = true) {
    return this.raw([ESC, 0x45, on ? 1 : 0]);
  }

  /** ESC - n */
  underline(on = true) {
    return this.raw([ESC, 0x2d, on ? 1 : 0]);
  }

  /** GS B n — white on black. */
  invert(on = true) {
    return this.raw([GS, 0x42, on ? 1 : 0]);
  }

  /** GS ! n — character width/height multiplier 1–8. */
  size(width = 1, height = 1) {
    const w = Math.min(8, Math.max(1, width)) - 1;
    const h = Math.min(8, Math.max(1, height)) - 1;
    return this.raw([GS, 0x21, (w << 4) | h]);
  }

  /** ESC d n — print and feed n lines. */
  feed(lines = 1) {
    return this.raw([ESC, 0x64, Math.min(255, Math.max(0, lines))]);
  }

  /** GS V — 'full' | 'partial'. */
  cut(type = 'partial') {
    return this.raw([GS, 0x56, type === 'full' ? 0x00 : 0x01]);
  }

  /** ESC p m t1 t2 — pulse the cash drawer on connector pin 2 or 5. */
  drawer(pin = 2) {
    return this.raw([ESC, 0x70, pin === 5 ? 1 : 0, 0x19, 0xfa]);
  }

  /**
   * GS ( K fn=49 — print density (Epson and most clones; other printers ignore it).
   * level −6…6, 0 = printer default.
   */
  density(level = 0) {
    const n = Math.max(-6, Math.min(6, Math.round(level)));
    if (n === 0) return this;
    return this.raw([GS, 0x28, 0x4b, 0x02, 0x00, 0x31, n < 0 ? 256 + n : n]);
  }

  /**
   * GS v 0 — raster bit image. Sent in bands of ≤ 128 rows: cheap printers have small
   * receive buffers and drop or garble taller images.
   */
  image(bitmap, { band = 128 } = {}) {
    const rowBytes = Math.ceil(bitmap.width / 8);
    for (let y = 0; y < bitmap.height; y += band) {
      const rows = Math.min(band, bitmap.height - y);
      this.raw([GS, 0x76, 0x30, 0x00, rowBytes & 0xff, rowBytes >> 8, rows & 0xff, rows >> 8]);
      const start = y * rowBytes;
      this.raw(bitmap.data.subarray(start, start + rows * rowBytes));
    }
    return this;
  }

  /** Native QR (GS ( k, model 2). size = module size 1–16, ec = L | M | Q | H. */
  qr(data, { size = 6, ec = 'M' } = {}) {
    const bytes = new TextEncoder().encode(data);
    const ecByte = { L: 48, M: 49, Q: 50, H: 51 }[ec] ?? 49;
    const len = bytes.length + 3;
    return this.raw([GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00])
      .raw([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, Math.min(16, Math.max(1, size))])
      .raw([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, ecByte])
      .raw([GS, 0x28, 0x6b, len & 0xff, len >> 8, 0x31, 0x50, 0x30])
      .raw(bytes)
      .raw([GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30]);
  }

  /** CODE128 (code set B) with human-readable text below. */
  barcode(data, { height = 60, width = 2 } = {}) {
    const payload = encodeText(`{B${data}`, 'cp437');
    return this.raw([GS, 0x68, Math.min(255, Math.max(1, height))])
      .raw([GS, 0x77, Math.min(6, Math.max(2, width))])
      .raw([GS, 0x48, 0x02])
      .raw([GS, 0x6b, 0x49, payload.length])
      .raw(payload);
  }

  bytes() {
    return Uint8Array.from(this.parts);
  }
}

/** DLE EOT n — real-time status request (1 printer, 2 offline cause, 4 paper sensor). */
export function statusRequest(n) {
  return Uint8Array.of(DLE, 0x04, n);
}

/**
 * Interpret DLE EOT replies. `replies` maps n → the single status byte the printer sent.
 * Returns { online, coverOpen, paperOut, paperLow, error, state } (missing replies → undefined).
 */
export function parseStatus(replies) {
  const out = {};
  if (replies[1] != null) out.online = (replies[1] & 0x08) === 0;
  if (replies[2] != null) {
    out.coverOpen = (replies[2] & 0x04) !== 0;
    out.paperOut = (replies[2] & 0x20) !== 0;
    out.error = (replies[2] & 0x40) !== 0;
  }
  if (replies[4] != null) {
    out.paperLow = (replies[4] & 0x0c) !== 0;
    out.paperOut = Boolean(out.paperOut) || (replies[4] & 0x60) !== 0;
  }
  out.state =
    out.online === false || out.coverOpen || out.paperOut || out.error ? 'error' : out.paperLow ? 'warning' : 'online';
  return out;
}

export function statusMessage(s) {
  if (!s) return 'Unknown';
  if (s.paperOut) return 'Out of paper';
  if (s.coverOpen) return 'Cover open';
  if (s.error) return 'Printer error';
  if (s.online === false) return 'Offline';
  if (s.paperLow) return 'Paper running low';
  return 'Ready';
}
